import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Search, ChevronDown, Check, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface LocationSelectProps {
    selectedLocations: string[];
    onChange: (locations: string[]) => void;
}

// Popular locations (always shown first)
const POPULAR_LOCATIONS = [
    'Dubai Marina',
    'Palm Jumeirah',
    'Business Bay',
    'Downtown Dubai',
    'Dubai Hills Estate',
    'Design District',
    'Jumeirah Village Circle',
    'Emaar Beachfront',
    'Meydan',
    'Port De La Mer'
];

export const LocationSelect: React.FC<LocationSelectProps> = ({ selectedLocations, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [allLocations, setAllLocations] = useState<string[]>(POPULAR_LOCATIONS);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Fetch unique locations from database on mount
    useEffect(() => {
        const fetchLocations = async () => {
            setIsLoadingLocations(true);
            try {
                const { data, error } = await supabase
                    .from('projects_canonical')
                    .select('district')
                    .not('district', 'is', null);

                if (!error && data) {
                    // Get unique districts, filter out nulls, and sort
                    const uniqueDistricts = Array.from(new Set(
                        data
                            .map((row: any) => row.district)
                            .filter((d: string | null): d is string => d !== null && d.trim() !== '')
                    )).sort();

                    // Merge with popular locations (popular first, then others)
                    const popularSet = new Set(POPULAR_LOCATIONS);
                    const otherLocations = uniqueDistricts.filter(d => !popularSet.has(d));
                    const merged = [...POPULAR_LOCATIONS, ...otherLocations];
                    
                    setAllLocations(merged);
                }
            } catch (err) {
                console.error('Failed to fetch locations:', err);
                // Fallback to popular locations only
            } finally {
                setIsLoadingLocations(false);
            }
        };

        fetchLocations();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleLocation = (loc: string) => {
        if (selectedLocations.includes(loc)) {
            onChange(selectedLocations.filter(l => l !== loc));
        } else {
            onChange([...selectedLocations, loc]);
        }
    };

    const filteredLocations = allLocations.filter(l =>
        l.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div ref={containerRef} className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium outline-none cursor-pointer border transition-all flex items-center justify-between ${isOpen ? 'bg-white border-slate-900 ring-1 ring-slate-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-white'}`}
            >
                <span className={`truncate ${selectedLocations.length > 0 ? 'text-slate-900 font-bold' : 'text-slate-500'}`}>
                    {selectedLocations.length === 0
                        ? "Select Locations"
                        : selectedLocations.length === 1
                            ? selectedLocations[0]
                            : `${selectedLocations.length} Selected`}
                </span>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 z-50 p-2 animate-[fadeIn_0.1s_ease-out]">
                    <div className="relative mb-2">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-8 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:ring-2 focus:ring-slate-900/10 outline-none"
                            autoFocus
                        />
                        {search && (
                            <button
                                onClick={() => setSearch('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                                <X size={12} />
                            </button>
                        )}
                    </div>

                    <div className="max-h-48 overflow-y-auto space-y-0.5">
                        {filteredLocations.map(loc => {
                            const isSelected = selectedLocations.includes(loc);
                            return (
                                <div
                                    key={loc}
                                    onClick={() => toggleLocation(loc)}
                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors group ${isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                                >
                                    <div className={`size-4 rounded border flex items-center justify-center transition-all ${isSelected ? 'bg-slate-900 border-slate-900' : 'border-slate-300 bg-white group-hover:border-slate-400'}`}>
                                        {isSelected && <Check size={10} className="text-white" />}
                                    </div>
                                    <span className={`text-xs font-medium ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                                        {loc}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
